// The outbox: this device's changes on their way to the relay (PROTOCOL.md §5, client rules). Platform-neutral: the
// documents, the store and the connection are interfaces, so the browser app and the CLI can both use it.
//
// - Edits mark documents changed; after 2 s without an edit (or at once on `flush`, which the page calls when it's
//   hidden or closed) each changed document's new local changes become one batch — `A.getChanges` since what was last
//   batched, this device's own only (another device's changes merged meanwhile are never sent back).
// - A batch is sealed with the project key and stored before it's sent, so nothing is ever kept in plain text and a
//   closed tab or a reload loses nothing that was batched. Each has a `cid`, kept with it, so sending it again after a
//   reconnect is idempotent (the relay answers `dup`).
// - Batches go in the order they were made; one is removed from the store only after the relay acknowledges it (and
//   after `onAcked` has kept it as the entry the relay numbered).
// - A document new here (a fragment made in the browser) goes first as a snapshot, as the relay requires. Once the
//   changes since a document's last snapshot pass 256 KB, a snapshot (`base` = the cursor the replica has merged) goes
//   instead of the batch.
// - `staleEpoch`, `noSnapshot` and the relay's other refusals are reported and the document's items held: never retried
//   in a loop (a new session tries once more). `staleEpoch` (a snapshot of an older history than the relay's) also asks
//   the owner to fetch the newer one (`onStale`), whose adoption replaces the refused item.
// - Epochs (W4e): each item carries the epoch and lineage it was built on, and is marked `sent` (stored) before its first
//   send, so after a reload an item that may have reached the relay is told from one that can't have. Before a newer
//   epoch is adopted, `settle` holds the document and waits for any item that may be on the relay to be acknowledged
//   (it's then synced, and absorbed by whoever holds the new epoch — never rebased here); `replace` then stages the
//   adoption's changes as one new-epoch item and drops the older-epoch items that were never sent (their edits are in
//   those changes), and the document's baseline moves on.
// - Key rotation (W9a, PROTOCOL.md §10): the relay refuses an item sealed with a key the project no longer has
//   (`keyChanged`). It's kept, and the owner is told (`onKeyChanged`); once it has the new key, `rekey` opens every kept
//   item with the old key and seals it with the new one (stored again, same cid), and they go again. Nothing is dropped.
// - "This device's changes" are those made with any actor this session has written with, not only the document's
//   current one: since W9c a shared project's edits switch a document to the session's actor, and a batch that held
//   changes from before and after the switch dropped the earlier ones (and the later ones then waited on them for ever).
import * as A from '@automerge/automerge';
import { concat } from '../shared/b64.ts';
import { fingerprint, open as openWithKey, seal as sealWithKey } from '../shared/crypto.ts';
import type { ServerMessage } from '../shared/wire.ts';
import { newCid, type AppendFields } from './sync.ts';

export const IDLE_MS = 2000;
export const SNAPSHOT_AFTER = 256 * 1024;
/** While someone else is in a document (PROTOCOL.md §10.12): its changes go after this pause, and at most `FAST_MAX_MS`
 *  after the first of them, so a co-writer sees typing within half a second. */
export const FAST_IDLE_MS = 150;
export const FAST_MAX_MS = 300;

export interface OutboxItem {
  /** Local order: items are sent in this order. */
  seq: number;
  cid: string;
  doc: string;
  kind: 'push' | 'snapshot';
  epoch: number;
  lineage: string;
  /** Snapshots: the heads (as the Swift encodes them) and the cursor the replica had merged. */
  heads?: string;
  base?: number;
  /** The Automerge bytes, sealed with the project key. */
  sealed: Uint8Array;
  /** Unsealed length, for the 256 KB rule. */
  size: number;
  createdAt: number;
  /** The relay refused it: kept, and not sent again this session (a `quota` refusal: until the next connection).
   *  `limit`/`used`: a quota refusal's figures, when the relay gave them. */
  refused?: { code: string; message: string; limit?: number; used?: number };
  /** Offered to the relay at least once (stored before the first send): it may be there even if no ack came back. */
  sent?: boolean;
  /** The hashes of the changes it carries (a push), or its heads (a snapshot): synced once acknowledged. */
  changes?: string[];
  /** The cids of unsent items this one took the place of (an epoch adoption's rebase): dropped on sight. */
  replaces?: string[];
}

/** Where batches wait (IndexedDB in the app, a file for the CLI, memory in tests). */
export interface OutboxStore {
  load(): Promise<OutboxItem[]>;
  /** Stores (or replaces) an item; false when it couldn't be kept. Keyed by seq and cid together, so two sessions
   *  of one project (two tabs) never overwrite each other's items. */
  put(item: OutboxItem): Promise<boolean>;
  remove(item: OutboxItem): Promise<void>;
}

/** The store key of an item: its order, then its cid. */
export const outboxKey = (item: Pick<OutboxItem, 'seq' | 'cid'>) => `${String(item.seq).padStart(10, '0')}:${item.cid}`;

export type AppendResult = Extract<ServerMessage, { t: 'ack' | 'error' }>;

/** The relay connection as the outbox needs it (a `SyncConnection` with its limits). */
export interface OutboxTransport {
  append(kind: 'push' | 'snapshot', fields: AppendFields, sealed: Uint8Array): Promise<AppendResult>;
}

export interface OutboxDoc {
  doc: A.Doc<unknown>;
  epoch: number;
  lineage: string;
}

/** The live documents (the replica's) and how far the replica has merged the relay's log. */
export interface OutboxSource {
  get(name: string): OutboxDoc | undefined;
  names(): Iterable<string>;
  cursor(): number;
}

export interface OutboxStatus {
  /** Documents with edits not yet batched and stored (a closed page would lose them). */
  unsaved: number;
  /** Batches stored here that the relay hasn't acknowledged yet. */
  pending: number;
  sending: boolean;
  connected: boolean;
  refused: { doc: string; code: string; message: string; limit?: number; used?: number }[];
  /** When the last batch went to the relay, and when the last acknowledgement came back (ms since 1970). */
  lastSentAt: number | null;
  lastAckAt: number | null;
}

export interface Timers {
  set(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

export interface OutboxOptions {
  key: Uint8Array;
  store: OutboxStore;
  source: OutboxSource;
  seal?: (key: Uint8Array, bytes: Uint8Array) => Promise<Uint8Array>;
  idleMs?: number;
  /** The pause (and the longest wait) for documents another member is in (`setQuick`). */
  fastIdleMs?: number;
  fastMaxMs?: number;
  snapshotAfter?: number;
  /** The app's save, called with the documents about to be batched before their changes are taken: it may write to
   *  them (the day's word log, `modifiedAt`), and those writes go in the same batch. */
  beforeBatch?: (docs: string[]) => void;
  /** The relay has an item (acknowledged, or already had it: `dup`), numbered `seq` in its log. */
  onAcked?: (item: OutboxItem, seq: number, dup: boolean) => Promise<void> | void;
  /** The relay holds a newer epoch of this document than an item was built on (`staleEpoch`): fetch it. */
  onStale?: (doc: string, epoch: number, lineage: string) => void;
  /** The relay refused an item because the project's key changed (`keyChanged`): fetch the new key and `rekey`. */
  onKeyChanged?: () => void;
  onStatus?: (status: OutboxStatus) => void;
  timers?: Timers;
  newCid?: () => string;
  now?: () => number;
}

/** Heads as the Swift encodes them (`Heads.encode`): sorted lowercase hex, joined. */
const encodeHeads = (heads: string[]) => [...heads].map((h) => h.toLowerCase()).sort().join('');

const REFUSALS = new Set(['staleEpoch', 'noSnapshot', 'invalid', 'forbidden', 'blobMissing', 'quota', 'keyChanged', 'ownerOnly']);

/** The relay's quota refusal of a blob upload (PROTOCOL.md §9: 413/403 `{error: "quota", limit, used}`), as the
 *  connection reports it (an `ApiError`-like `code`, or its HTTP error text): the refusal to keep, or null. */
export function quotaRefusal(e: unknown): { code: 'quota'; message: string; limit?: number; used?: number } | null {
  if (!e || typeof e !== 'object') return null;
  const x = e as { code?: unknown; status?: unknown; message?: unknown; limit?: unknown; used?: unknown; details?: { limit?: unknown; used?: unknown } };
  const figures = (o: { limit?: unknown; used?: unknown }) => ({
    ...(typeof o.limit === 'number' ? { limit: o.limit } : {}),
    ...(typeof o.used === 'number' ? { used: o.used } : {}),
  });
  if (x.code === 'quota') return { code: 'quota', message: typeof x.message === 'string' ? x.message : 'quota', ...figures(x.details ?? x) };
  const text = typeof x.message === 'string' ? x.message : '';
  const m = /\b(?:403|413)\b\s*(\{.*\})\s*$/s.exec(text);
  if (!m) return null;
  try {
    const body = JSON.parse(m[1]) as { error?: unknown; message?: unknown; limit?: unknown; used?: unknown };
    if (body.error !== 'quota') return null;
    return { code: 'quota', message: typeof body.message === 'string' ? body.message : 'quota', ...figures(body) };
  } catch {
    return null;
  }
}

/** Changes (or any byte strings) end to end. */
function concatAll(parts: Uint8Array[]): Uint8Array {
  return parts.length === 1 ? parts[0] : concat(...parts);
}

export class Outbox {
  private readonly baseline = new Map<string, A.Heads>();
  private readonly sizeSince = new Map<string, number>();
  private readonly dirty = new Set<string>();
  private items: OutboxItem[] = [];
  private nextSeq = 1;
  private staging: Promise<void> = Promise.resolve();
  private stagingCount = 0;
  private timer: unknown = null;
  private transport: OutboxTransport | null = null;
  private sending = false;
  private lastSentAt: number | null = null;
  private lastAckAt: number | null = null;
  private readonly blocked = new Map<string, { code: string; message: string }>();
  private readonly timers: Timers;
  private stopped = false;
  /** Documents being adopted: nothing more of theirs is sent until `replace` (or `release`). */
  private readonly held = new Set<string>();
  /** The item awaiting the relay's reply. */
  private inFlight: OutboxItem | null = null;
  /** Items the relay acknowledged whose `onAcked` hasn't finished (they're done as far as `settle` is concerned). */
  private readonly acked = new Set<OutboxItem>();
  /** Called whenever an item is answered or the connection changes (`settle` waits on it). */
  private wake: (() => void)[] = [];

  /** Documents another member is in now (presence): their changes go after the short pause. */
  private quick = new Set<string>();
  /** When the first change not yet batched was made (the short pause's longest wait counts from it). */
  private firstChange: number | null = null;

  /** Every actor this session's documents have written with (a document's actor is always this device's own: loaded
   *  documents get a fresh one). Changes by these are this device's to send. */
  private readonly local = new Set<string>();

  /** The project key items are sealed with now (`rekey` replaces it). */
  private key: Uint8Array;
  /** The key's fingerprint, sent with each append so the relay can refuse an old key's (`keyChanged`). */
  private fp: Promise<string>;

  constructor(private readonly o: OutboxOptions) {
    this.timers = o.timers ?? { set: (fn, ms) => setTimeout(fn, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>) };
    this.key = o.key;
    this.fp = fingerprint(o.key);
  }

  /**
   * The project's key changed (rotation): every kept item is opened with the old key (or whichever of `previous` opens
   * it), sealed with the new one and stored again under the same cid; `keyChanged` refusals are lifted and they go again.
   * An item no key opens is left as it is (and stays refused). Resolves once every item is stored.
   */
  async rekey(key: Uint8Array, previous: Uint8Array[] = []): Promise<void> {
    await this.staging;
    const old = [this.key, ...previous];
    const seal = this.o.seal ?? sealWithKey;
    this.key = key;
    this.fp = fingerprint(key);
    for (const item of [...this.items]) {
      if (item === this.inFlight) continue; // answered soon: a `keyChanged` refusal brings it back here
      let plain: Uint8Array | null = null;
      for (const k of old) {
        try {
          plain = await openWithKey(k, item.sealed);
          break;
        } catch {
          // another key's
        }
      }
      if (!plain) {
        try {
          await openWithKey(key, item.sealed);
          if (item.refused?.code === 'keyChanged') item.refused = undefined; // sealed with the new key already
        } catch {
          // no key here opens it: left refused
        }
        continue;
      }
      item.sealed = (await seal(key, plain)) as Uint8Array;
      if (item.refused?.code === 'keyChanged') item.refused = undefined;
      await this.o.store.put(item).catch(() => false);
    }
    for (const [doc, r] of [...this.blocked]) if (r.code === 'keyChanged') this.blocked.delete(doc);
    this.emit();
    this.wakeAll();
    if (this.transport) void this.pump();
  }

  /** The key items are sealed with now. */
  get currentKey(): Uint8Array {
    return this.key;
  }

  /** Items kept from an earlier session, in order (the app applies them to its replica before `start`), without those
   *  an adoption's rebase replaced. */
  static async pending(store: OutboxStore): Promise<OutboxItem[]> {
    const all = (await store.load()).sort((a, b) => a.seq - b.seq || a.createdAt - b.createdAt);
    const replaced = new Set(all.flatMap((i) => i.replaces ?? []));
    return all.filter((i) => !replaced.has(i.cid));
  }

  /**
   * Starts from the store's pending items and the documents as they are now: everything in them counts as batched
   * already (earlier sessions' changes are in the store or on the relay). `sizes`: entries the replica was built from,
   * for the changes-since-snapshot count.
   */
  async start(sizes: { doc: string; kind: 'snapshot' | 'batch'; seq: number; size: number }[] = []): Promise<void> {
    this.items = (await Outbox.pending(this.o.store)).map((i) => ({ ...i, refused: undefined }));
    // An adoption's rebase took these unsent items' place, and the page closed before they were removed.
    const replaced = new Set(this.items.flatMap((i) => i.replaces ?? []));
    for (const i of this.items.filter((x) => replaced.has(x.cid))) await this.o.store.remove(i).catch(() => {});
    this.items = this.items.filter((i) => !replaced.has(i.cid));
    this.nextSeq = Math.max(0, ...this.items.map((i) => i.seq)) + 1;
    for (const name of this.o.source.names()) this.track(name);
    for (const e of [...sizes].sort((a, b) => a.seq - b.seq)) this.count(e.doc, e.kind, e.size);
    for (const i of this.items) this.count(i.doc, i.kind === 'snapshot' ? 'snapshot' : 'batch', i.size);
    this.emit();
  }

  stop() {
    this.stopped = true;
    if (this.timer !== null) this.timers.clear(this.timer);
    this.timer = null;
    this.transport = null;
  }

  private track(name: string) {
    const d = this.o.source.get(name);
    if (!d) return;
    this.local.add(A.getActorId(d.doc));
    if (!this.baseline.has(name)) this.baseline.set(name, A.getHeads(d.doc));
  }

  /** Actors this device wrote with (an edit that switched a document's actor names both). */
  ownActors(actors: Iterable<string>) {
    for (const a of actors) this.local.add(a);
  }

  private count(doc: string, kind: 'snapshot' | 'batch', size: number) {
    this.sizeSince.set(doc, kind === 'snapshot' ? 0 : (this.sizeSince.get(doc) ?? 0) + size);
  }

  /** Another device's entry was applied: it counts towards the document's changes since its snapshot, and a document
   *  first seen now is one the relay has. */
  remote(doc: string, kind: 'snapshot' | 'batch', size: number) {
    this.count(doc, kind, size);
    if (!this.dirty.has(doc)) this.track(doc);
  }

  /** Local edits to these documents: batched after the idle pause. */
  changed(names: Iterable<string>) {
    let any = false;
    for (const n of names) {
      this.dirty.add(n);
      const d = this.o.source.get(n);
      if (d) this.local.add(A.getActorId(d.doc));
      any = true;
    }
    if (!any || this.stopped) return;
    this.firstChange ??= this.o.now?.() ?? Date.now();
    this.armIdle();
    this.emit();
  }

  /**
   * The documents someone else is in now (W9b): while one of them has changes waiting, the batch goes after
   * `fastIdleMs` without an edit (at most `fastMaxMs` after the first) instead of the usual pause. Empty: back to normal.
   */
  setQuick(docs: Iterable<string>) {
    const next = new Set(docs);
    if (next.size === this.quick.size && [...next].every((d) => this.quick.has(d))) return;
    this.quick = next;
    if (this.timer !== null && !this.stopped) this.armIdle();
  }

  /** Whether changes to `doc` go after the short pause now. */
  isQuick(doc: string): boolean {
    return this.quick.has(doc);
  }

  /** (Re)starts the pause before batching: short while a changed document has company, else the usual. */
  private armIdle() {
    if (this.timer !== null) this.timers.clear(this.timer);
    const now = this.o.now?.() ?? Date.now();
    const fast = [...this.dirty].some((d) => this.quick.has(d));
    const wait = fast
      ? Math.max(0, Math.min(this.o.fastIdleMs ?? FAST_IDLE_MS, (this.firstChange ?? now) + (this.o.fastMaxMs ?? FAST_MAX_MS) - now))
      : this.o.idleMs ?? IDLE_MS;
    this.timer = this.timers.set(() => {
      this.timer = null;
      void this.flush();
    }, wait);
  }

  /** Batches what's changed now (sealed and stored), then sends. Resolves once it's stored. */
  flush(): Promise<void> {
    if (this.timer !== null) this.timers.clear(this.timer);
    this.timer = null;
    const done = this.stage();
    void done.then(() => this.pump());
    return done;
  }

  /** The connection, once the replica has caught up (null when it closes). Pending items go at once, in order. */
  connected(transport: OutboxTransport | null) {
    this.transport = transport;
    // A quota refusal holds a document until the next connection (the writer may have made room since): its items are
    // offered once more then, never in a loop.
    if (transport) this.unblock('quota');
    this.emit();
    this.wakeAll();
    if (transport) void this.pump();
  }

  private unblock(code: string) {
    for (const [doc, r] of [...this.blocked]) if (r.code === code) this.blocked.delete(doc);
    for (const i of this.items) if (i.refused?.code === code) i.refused = undefined;
  }

  private wakeAll() {
    const w = this.wake;
    this.wake = [];
    for (const fn of w) fn();
  }

  /** Items of `doc` that may be on the relay without an acknowledgement here. */
  uncertain(doc: string): OutboxItem[] {
    return this.items.filter((i) => i.doc === doc && (i.sent || i === this.inFlight) && !i.refused && !this.acked.has(i));
  }

  /**
   * Before a newer epoch replaces `doc`: holds the document (nothing more of it is sent) and waits until every item of it
   * that may have reached the relay is acknowledged. True when that's so; false when it can't be now (no connection),
   * and the hold is released.
   */
  async settle(doc: string): Promise<boolean> {
    this.held.add(doc);
    await this.staging;
    for (;;) {
      if (!this.uncertain(doc).length) return true;
      if (!this.transport || this.stopped) {
        this.release(doc);
        return false;
      }
      // Only this document's uncertain items go now; nothing new of it starts after them.
      await new Promise<void>((resolve) => {
        this.wake.push(resolve);
        void this.pump();
      });
    }
  }

  /** Lets a held document's items go again (an adoption that didn't happen). */
  release(doc: string) {
    if (this.held.delete(doc) && this.transport) void this.pump();
  }

  /**
   * A newer epoch replaced `doc` here: `changes` (the adoption's absorbed and rebased changes) are staged as one push of
   * the new epoch, and the older-epoch items of `doc` never sent (or refused) are dropped — their edits are in those
   * changes — once it's stored. The document's baseline moves to `heads` and it's released.
   */
  async replace(doc: string, o: { changes: Uint8Array[]; hashes: string[]; epoch: number; lineage: string; heads: A.Heads }): Promise<void> {
    await this.staging;
    const older = this.items.filter((i) => i.doc === doc && (i.epoch !== o.epoch || i.lineage !== o.lineage) && (!i.sent || i.refused) && i !== this.inFlight);
    this.items = this.items.filter((i) => !older.includes(i));
    this.baseline.set(doc, [...o.heads]);
    this.sizeSince.set(doc, 0); // a new snapshot: the size rule counts from it
    this.blocked.delete(doc);
    this.held.delete(doc);
    if (o.changes.length) await this.stageExplicit(doc, o.changes, o.hashes, o.epoch, o.lineage, older.map((i) => i.cid));
    for (const i of older) await this.o.store.remove(i).catch(() => {});
    this.emit();
    if (this.transport) void this.pump();
  }

  private async stageExplicit(doc: string, changes: Uint8Array[], hashes: string[], epoch: number, lineage: string, replaces?: string[]) {
    const bytes = concatAll(changes);
    const now = this.o.now?.() ?? Date.now();
    const made: Omit<OutboxItem, 'sealed'> = {
      seq: this.nextSeq++, cid: (this.o.newCid ?? newCid)(), doc, kind: 'push', epoch, lineage, size: bytes.length, createdAt: now,
      changes: hashes, ...(replaces?.length ? { replaces } : {}),
    };
    this.count(doc, 'batch', bytes.length);
    this.stagingCount += 1;
    const seal = this.o.seal ?? sealWithKey;
    const sealing = seal(this.key, bytes);
    this.staging = this.staging.then(async () => {
      try {
        const item: OutboxItem = { ...made, sealed: await sealing };
        await this.o.store.put(item).catch(() => false);
        this.items.push(item);
      } finally {
        this.stagingCount -= 1;
        this.emit();
      }
    });
    await this.staging;
  }

  /** Whether a closed page would lose edits (not yet batched and stored). */
  hasUnsaved(): boolean {
    return this.dirty.size > 0 || this.stagingCount > 0;
  }

  status(): OutboxStatus {
    return {
      unsaved: this.dirty.size + this.stagingCount,
      pending: this.items.length,
      sending: this.sending,
      connected: !!this.transport,
      refused: this.items.filter((i) => i.refused).map((i) => ({ doc: i.doc, ...i.refused! })),
      lastSentAt: this.lastSentAt,
      lastAckAt: this.lastAckAt,
    };
  }

  private emit() {
    this.o.onStatus?.(this.status());
  }

  /** Takes each changed document's new local changes (synchronously, so later edits start a new batch), then seals
   *  and stores them in order. */
  private stage(): Promise<void> {
    if (!this.dirty.size) return this.staging;
    this.o.beforeBatch?.([...this.dirty]);
    const names = [...this.dirty];
    this.dirty.clear();
    this.firstChange = null;
    const made: Omit<OutboxItem, 'sealed'>[] = [];
    const plain: Uint8Array[] = [];
    const now = this.o.now?.() ?? Date.now();
    for (const name of names) {
      const d = this.o.source.get(name);
      if (!d) continue;
      const heads = A.getHeads(d.doc);
      const base = this.baseline.get(name);
      let bytes: Uint8Array | null = null;
      let hashes: string[] = [];
      let snapshot = base === undefined;
      if (base !== undefined) {
        if (!A.hasHeads(d.doc, base)) {
          // Replaced by another history (a newer epoch was adopted): nothing of this device's is in it.
          this.baseline.set(name, heads);
          continue;
        }
        this.local.add(A.getActorId(d.doc));
        const backend = A.getBackend(d.doc);
        const mine = A.getChangesMetaSince(d.doc, base).filter((m) => this.local.has(m.actor));
        this.baseline.set(name, heads);
        if (!mine.length) continue;
        bytes = concat(...mine.map((m) => backend.getChangeByHash(m.hash)!));
        hashes = mine.map((m) => m.hash);
        snapshot = (this.sizeSince.get(name) ?? 0) + bytes.length > (this.o.snapshotAfter ?? SNAPSHOT_AFTER);
      }
      this.baseline.set(name, heads);
      if (snapshot) bytes = A.save(d.doc);
      const item: Omit<OutboxItem, 'sealed'> = {
        seq: this.nextSeq++, cid: (this.o.newCid ?? newCid)(), doc: name, kind: snapshot ? 'snapshot' : 'push', epoch: d.epoch,
        lineage: d.lineage, size: bytes!.length, createdAt: now, changes: snapshot ? [...heads] : hashes,
        ...(snapshot ? { heads: encodeHeads(heads), base: this.o.source.cursor() } : {}),
      };
      this.count(name, snapshot ? 'snapshot' : 'batch', bytes!.length);
      made.push(item);
      plain.push(bytes!);
    }
    if (!made.length) {
      this.emit();
      return this.staging;
    }
    this.stagingCount += 1;
    this.emit();
    const seal = this.o.seal ?? sealWithKey;
    const key = this.key;
    const sealing = Promise.all(plain.map((b) => seal(key, b)));
    this.staging = this.staging.then(async () => {
      try {
        const sealed = await sealing;
        for (let i = 0; i < made.length; i++) {
          const item: OutboxItem = { ...made[i], sealed: sealed[i] };
          // Kept before it's sent (and in memory even if the store refuses it, so it still goes).
          await this.o.store.put(item).catch(() => false);
          this.items.push(item);
        }
      } finally {
        this.stagingCount -= 1;
        this.emit();
      }
    });
    return this.staging;
  }

  /** Sends pending items in order until the queue is empty or the connection goes. */
  private async pump(): Promise<void> {
    if (this.sending || !this.transport || this.stopped) return;
    this.sending = true;
    this.emit();
    try {
      for (;;) {
        const transport = this.transport;
        if (!transport || this.stopped) break;
        const item = this.items.find((i) => !i.refused && !this.blocked.has(i.doc) && !this.acked.has(i)
          && (!this.held.has(i.doc) || (i.sent && this.uncertain(i.doc).includes(i))));
        if (!item) break;
        if (!item.sent) {
          // Marked (and kept) before it goes: after a reload it may be on the relay even without an ack.
          item.sent = true;
          await this.o.store.put(item).catch(() => false);
        }
        const fields: AppendFields = {
          doc: item.doc, epoch: item.epoch, lineage: item.lineage, cid: item.cid,
          ...(item.kind === 'snapshot' ? { heads: item.heads, base: item.base } : {}),
          keyFingerprint: await this.fp,
        };
        let reply: AppendResult;
        try {
          this.lastSentAt = this.o.now?.() ?? Date.now();
          this.inFlight = item;
          reply = await transport.append(item.kind, fields, item.sealed);
        } catch (e) {
          this.inFlight = null;
          // A blob the relay wouldn't take for quota: refused (and kept), as an error frame's `quota` is.
          const quota = quotaRefusal(e);
          if (quota) {
            item.refused = quota;
            this.blocked.set(item.doc, quota);
            await this.o.store.put(item).catch(() => false);
            this.wakeAll();
            this.emit();
            continue;
          }
          // The connection went: the item stays, and goes again (same cid) when it's back.
          if (this.transport === transport) this.transport = null;
          this.wakeAll();
          break;
        }
        this.inFlight = null;
        if (reply.t === 'ack') {
          // Settled as soon as the relay answers (an adoption waiting on it mustn't wait for `onAcked`'s own work).
          this.acked.add(item);
          this.wakeAll();
          try {
            await this.o.onAcked?.(item, reply.seq, reply.dup);
          } finally {
            await this.o.store.remove(item).catch(() => {});
            this.items = this.items.filter((i) => i !== item);
            this.acked.delete(item);
          }
          this.lastAckAt = this.o.now?.() ?? Date.now();
        } else if (REFUSALS.has(reply.code)) {
          const figures = reply as { limit?: unknown; used?: unknown };
          const refused = {
            code: reply.code, message: reply.message,
            ...(typeof figures.limit === 'number' ? { limit: figures.limit } : {}),
            ...(typeof figures.used === 'number' ? { used: figures.used } : {}),
          };
          item.refused = refused;
          this.blocked.set(item.doc, refused);
          await this.o.store.put(item).catch(() => false);
          this.wakeAll();
          if (reply.code === 'staleEpoch') this.o.onStale?.(item.doc, reply.epoch ?? item.epoch, reply.lineage ?? item.lineage);
          if (reply.code === 'keyChanged') this.o.onKeyChanged?.();
        } else {
          this.wakeAll();
          break; // an error this outbox doesn't know: stop, and try again with the next connection
        }
        this.emit();
      }
    } finally {
      this.sending = false;
      this.emit();
      this.wakeAll();
    }
  }
}

/** Batches at once when the page is hidden or closed (`visibilitychange` to hidden, `pagehide`). Returns a detach. */
export function flushWhenHidden(outbox: Outbox, target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>, visible: () => boolean): () => void {
  const onVisibility = () => void (!visible() && outbox.flush());
  const onHide = () => void outbox.flush();
  target.addEventListener('visibilitychange', onVisibility);
  target.addEventListener('pagehide', onHide);
  return () => {
    target.removeEventListener('visibilitychange', onVisibility);
    target.removeEventListener('pagehide', onHide);
  };
}

/** An in-memory store (tests; a "don't keep on this computer" session). */
export class MemoryOutboxStore implements OutboxStore {
  readonly items = new Map<string, OutboxItem>();
  async load() {
    return [...this.items.values()].map((i) => ({ ...i }));
  }
  async put(item: OutboxItem) {
    this.items.set(outboxKey(item), { ...item });
    return true;
  }
  async remove(item: OutboxItem) {
    this.items.delete(outboxKey(item));
  }
}
